import React, {
  FC,
} from 'react';
import {
  Control,
  Controller,
  SubmitErrorHandler,
  UseFormHandleSubmit,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import FillingStepNav from 'views/EMR/components/FillableForm/components/FillingStepNav';
import {
  FillableFormField,
  FormDynamicField,
  FormFieldValue,
  FormFieldsData,
} from 'views/EMR/components/FillableForm/types';
import FormFieldWizardLabel from 'views/EMR/components/FillableForm/components/FormFieldWizardLabel';
import {
  getFormFieldValidationMessage,
} from 'views/EMR/components/FillableForm/utils/fieldValidation';
import {
  getStableKey,
  isFormFieldStrictlyRequired,
} from 'views/EMR/components/FillableForm/utils/fieldPositioning';

type FieldComponentProps = {
  field: FillableFormField;
  value: FormDynamicField | undefined;
  onChange: (value: FormDynamicField | undefined) => void;
  error?: boolean;
};

type FieldComponent = FC<FieldComponentProps>;

type Props = {
  currentField: FillableFormField;
  currentFieldIndex: number;
  totalSteps: number;
  control: Control<FormFieldsData>;
  Component: FieldComponent;
  validate: (data: FormFieldValue, field: FillableFormField) => boolean;
  handleSubmit: UseFormHandleSubmit<FormFieldsData>;
  onSubmit: (data: FormFieldsData) => void;
  onInvalidSubmit?: SubmitErrorHandler<FormFieldsData>;
  onFieldChange?: (data: FormFieldValue) => void;
  onContinue?: () => void;
  onTrigger: (name?: string) => Promise<boolean>;
  onPrev: () => void;
  onNext: () => void;
  isLastFieldStep?: boolean;
  isSaving?: boolean;
};

export default function FieldWizardBar({
  currentField,
  currentFieldIndex,
  totalSteps,
  control,
  Component,
  validate,
  handleSubmit,
  onSubmit,
  onInvalidSubmit,
  onFieldChange,
  onContinue,
  onTrigger,
  onPrev,
  onNext,
  isLastFieldStep = false,
  isSaving = false,
}: Props) {
  const validationMessage = getFormFieldValidationMessage(currentField);

  return (
    <div className="flex flex-col gap-4 px-4 w-3/4 max-w-[870px]">
      <Controller
        key={getStableKey(currentField)}
        name={currentField?.id ?? ''}
        rules={{
          required: isFormFieldStrictlyRequired(currentField)
            ? validationMessage
            : false,
          validate: (data) => validate(data, currentField) || validationMessage,
        }}
        control={control}
        render={({ field: { ref: _ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
          <Wrapper
            error={invalid}
            errorText={error?.message}
            id={currentField?.id ?? ''}
            label={(
              <FormFieldWizardLabel
                field={currentField}
                id={currentField?.id ?? ''}
              />
            )}
            containerClassName="!cursor-default"
            className="flex-1 min-w-0"
          >
            <Component
              {...field}
              error={invalid}
              onChange={(nextValue) => {
                onChange(nextValue);
                onFieldChange?.(nextValue);
              }}
              value={value as FillableFormField}
              field={currentField}
            />
          </Wrapper>
        )}
      />
      <div className="flex gap-4 flex-1 items-center justify-between">
        <Button
          variant="contained"
          color="primary"
          disabled={isSaving}
          className="min-w-32 w-auto h-8 !py-0 !capitalize flex gap-2 items-center"
          onClick={isLastFieldStep ? handleSubmit(onSubmit, onInvalidSubmit) : onContinue}
          type="button"
        >
          {isSaving && <Spinner />}
          Continue
        </Button>
        <FillingStepNav
          currentStep={currentFieldIndex + 1}
          totalSteps={totalSteps}
          onPrev={onPrev}
          isNextDisabled={currentFieldIndex + 1 >= totalSteps}
          onNext={async () => {
            const valid = await onTrigger(currentField?.id ?? undefined);
            if (valid) {
              onNext();
            }
          }}
        />
      </div>

    </div>
  );
}
